import { useToast as useToastV2 } from "@circle-react-uikit/ToastV2";
import { useToast as useCompassToast } from "@circleco/compass/context/CompassContext";
/**
 * Toast Compatibility Layer
 *
 * Maps ToastV2 API to Compass Toast API when feature flag is enabled.
 *
 * For WebSocket-driven progress toasts, use the dedicated `useProgressToast`
 * hook from this directory instead.
 */
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { t } from "@/i18n-js/instance";
import { durations } from "@/react/components/shared/uikit/ToastV2/constants";
import type {
  Toast as ToastV2Toast,
  ToastDuration,
  ToastOptions as ToastV2Options,
  ToastPlacement,
} from "@/react/components/shared/uikit/ToastV2/interfaces";
import type { ToastContextProps as ToastV2ContextProps } from "@/react/components/shared/uikit/ToastV2/ToastProvider";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

import { extractTextFromReactNode } from "./toastUtils";

/* eslint-disable no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports toast utilities for SettingsApp */
export { ToastProvider } from "@circle-react-uikit/ToastV2";
/* eslint-enable no-barrel-files/no-barrel-files -- End of compat re-exports */

const mapDurationToTimeout = (duration?: ToastDuration): number | undefined => {
  if (!duration) {
    return undefined;
  }
  return durations[duration];
};

interface MapOptionsResult {
  isDismissible?: boolean;
  onRemove?: () => void;
  timeout?: number;
}

const mapOptions = (options?: ToastV2Options): MapOptionsResult => {
  if (!options) {
    return {};
  }

  const result: MapOptionsResult = {};

  if (options.duration) {
    result.timeout = mapDurationToTimeout(options.duration);
  }

  if (options.shouldAutohide === false) {
    result.timeout = 0;
  }

  if (options.shouldShowCloseButton !== undefined) {
    result.isDismissible = options.shouldShowCloseButton;
  }

  if (options.onComplete) {
    result.onRemove = options.onComplete;
  }

  return result;
};

const PROGRESS_ANIMATION_DURATION = 2000;
const FLASH_NOTICE_TEST_ID = { "data-testid": "flash-notice" } as const;

export const useToast = (): ToastV2ContextProps => {
  const shouldUseCompass = useIsCircleV4FeatureEnabled(
    "circle_v4_toast_enabled",
  );
  const compassToast = useCompassToast();
  const toastV2 = useToastV2();

  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(
    () => () => {
      clearTimeout(progressTimerRef.current);
    },
    [],
  );

  const success = useCallback(
    (message: ReactNode, options?: ToastV2Options): void => {
      if (shouldUseCompass) {
        try {
          const title =
            extractTextFromReactNode(message) ?? t("common.success");
          const hasMessageComplete = Boolean(options?.messageComplete);

          if (hasMessageComplete) {
            const toastId = compassToast.add({
              ...FLASH_NOTICE_TEST_ID,
              title,
              variant: "inverse",
              icon: "loading-circle",
              iconColor: "inverse",
              isDismissible: false,
              timeout: PROGRESS_ANIMATION_DURATION + durations.default,
            });

            progressTimerRef.current = setTimeout(() => {
              compassToast.update(toastId, {
                ...FLASH_NOTICE_TEST_ID,
                title: options?.messageComplete,
                variant: "primary",
                icon: "circle-check-filled",
                iconColor: "success",
                isDismissible: true,
                onRemove: options?.onComplete,
              });
            }, PROGRESS_ANIMATION_DURATION);
          } else {
            compassToast.add({
              ...FLASH_NOTICE_TEST_ID,
              title,
              variant: "primary",
              icon: "circle-check-filled",
              iconColor: "success",
              ...mapOptions(options),
            });
          }
          return;
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.error("[CompassCompat Toast] success fallback:", err);
          }
          toastV2.success(message, options);
          return;
        }
      }
      toastV2.success(message, options);
    },
    [shouldUseCompass, compassToast, toastV2],
  );

  const error = useCallback(
    (message: ReactNode, options?: ToastV2Options): void => {
      if (shouldUseCompass) {
        try {
          const title = extractTextFromReactNode(message) ?? t("common.error");
          compassToast.add({
            ...FLASH_NOTICE_TEST_ID,
            title,
            variant: "destructive",
            icon: "triangle-exclamation-filled",
            iconColor: "danger",
            ...mapOptions(options),
          });
          return;
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.error("[CompassCompat Toast] error fallback:", err);
          }
          toastV2.error(message, options);
          return;
        }
      }
      toastV2.error(message, options);
    },
    [shouldUseCompass, compassToast, toastV2],
  );

  const show = useCallback(
    (message: ReactNode, options?: ToastV2Options): string => {
      if (shouldUseCompass) {
        try {
          const title =
            extractTextFromReactNode(message) ?? t("common.notification");
          const variant =
            options?.variant === "danger" ? "destructive" : "primary";
          const icon =
            options?.variant === "danger"
              ? "triangle-exclamation-filled"
              : "circle-check-filled";
          const iconColor =
            options?.variant === "danger" ? "danger" : "success";

          return compassToast.add({
            ...FLASH_NOTICE_TEST_ID,
            title,
            variant,
            icon,
            iconColor,
            ...mapOptions(options),
          });
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.error("[CompassCompat Toast] show fallback:", err);
          }
          return toastV2.show(message, options);
        }
      }
      return toastV2.show(message, options);
    },
    [shouldUseCompass, compassToast, toastV2],
  );

  const remove = useCallback(
    (id: string): void => {
      if (shouldUseCompass) {
        try {
          compassToast.close(id);
          return;
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.error("[CompassCompat Toast] remove fallback:", err);
          }
          toastV2.remove(id);
          return;
        }
      }
      toastV2.remove(id);
    },
    [shouldUseCompass, compassToast, toastV2],
  );

  const toasts = useMemo((): ToastV2Toast[] => {
    if (shouldUseCompass) {
      return [];
    }
    return toastV2.toasts;
  }, [shouldUseCompass, toastV2.toasts]);

  const placement = useMemo((): ToastPlacement => {
    if (shouldUseCompass) {
      return "bottom";
    }
    return toastV2.placement;
  }, [shouldUseCompass, toastV2.placement]);

  return {
    success,
    error,
    show,
    remove,
    toasts,
    placement,
  };
};
